import React, { FC, ReactElement, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import ProfilePictureModal from "./ProfilePictureModal/ProfilePictureModal";
import ProfileHeaderModal from "./ProfileHeaderModal/ProfileHeaderModal";
import { ImageObj } from "../../../../components/AddTweetForm/AddTweetForm";
import ProfileDescriptionModal from "./ProfileDescriptionModal/ProfileDescriptionModal";
import ProfileUpdatedModal from "./ProfileUpdatedModal/ProfileUpdatedModal";
import { uploadImage } from "../../../../util/upload-image-helper";
import {
    selectUserProfileBirthday,
    selectUserProfileFullName,
    selectUserProfileLocation,
    selectUserProfileWebsite
} from "../../../../store/ducks/user/selectors";
import { updatedUserData } from "../../../../store/ducks/user/actionCreators";
import { useSetupProfileModalStyles } from "./SetupProfileModalStyles";
import { BtnLabelProps } from "../../../../types/user";
import { setOpenSnackBar } from "../../../../store/ducks/actionSnackbar/actionCreators";
import { ActionSnackbarStateType } from "../../../../store/ducks/actionSnackbar/contracts/state";

interface SetupProfileModalProps {
    visible: boolean;
    onClose: () => void;
}

const SetupProfileModal: FC<SetupProfileModalProps> = ({ visible, onClose }): ReactElement => {
    const classes = useSetupProfileModalStyles();
    const dispatch = useDispatch();
    const fullName = useSelector(selectUserProfileFullName);
    const location = useSelector(selectUserProfileLocation);
    const website = useSelector(selectUserProfileWebsite);
    const birthday = useSelector(selectUserProfileBirthday);
    const [visibleProfileHeaderModal, setVisibleProfileHeaderModal] = React.useState<boolean>(false);
    const [visibleProfileDescriptionModal, setVisibleProfileDescriptionModal] = React.useState<boolean>(false);
    const [visibleProfileUpdatedModal, setVisibleProfileUpdatedModal] = React.useState<boolean>(false);
    const [avatar, setAvatar] = useState<ImageObj>();
    const [wallpaper, setWallpaper] = useState<ImageObj>();
    const [bio, setBio] = useState<string>("");
    const [btnLabel, setBtnLabel] = useState<BtnLabelProps>({ label: "See profile", isDisabled: false });

    const handleCloseModal = (): void => {
        setVisibleProfileHeaderModal(false);
        setVisibleProfileDescriptionModal(false);
        setVisibleProfileUpdatedModal(false);
        onClose();
    };

    const onOpenProfileHeaderModal = (): void => {
        setVisibleProfileHeaderModal(true);
    };

    const onOpenProfileDescriptionModal = (): void => {
        setVisibleProfileDescriptionModal(true);
    };

    const onOpenProfileUpdatedModal = (): void => {
        setVisibleProfileUpdatedModal(true);
    };

    const onSubmit = async (): Promise<void> => {
        setBtnLabel({ label: "Loading...", isDisabled: true });
        let avatarResponse: string | undefined = undefined;
        let wallpaperResponse: string | undefined = undefined;
        // Upload avatar if it exists
        if (avatar) {
            const response: any = await uploadImage(avatar.file);
            if (!response?.success) {
                dispatch(
                    setOpenSnackBar({
                        message: response?.message || "Oops! Something went wrong!",
                        type: ActionSnackbarStateType.ERROR
                    })
                );
                closeModal();
                return;
            }
            avatarResponse = response?.data?.[0]?.src;
        }
        // Upload wallpaper if it exists
        if (wallpaper) {
            const response: any = await uploadImage(wallpaper.file);
            if (!response?.success) {
                dispatch(
                    setOpenSnackBar({
                        message: response?.message || "Oops! Something went wrong!",
                        type: ActionSnackbarStateType.ERROR
                    })
                );
                closeModal();
                return;
            }
            wallpaperResponse = response?.data?.[0]?.src;
        }

        dispatch(
            updatedUserData({
                fullName: fullName!,
                location: location!,
                website: website!,
                avatar: avatarResponse!,
                wallpaper: wallpaperResponse!,
                about: bio,
                birthday: birthday!
            })
        );
        closeModal();
    };

    const closeModal = () => {
        setBtnLabel({ label: "See profile", isDisabled: false });
        handleCloseModal();
    };

    return (
        <div className={classes.container}>
            <ProfilePictureModal
                isOpen={visible}
                onClose={handleCloseModal}
                avatar={avatar}
                onChangeAvatar={setAvatar}
                onOpenProfileHeaderModal={onOpenProfileHeaderModal}
            />
            <ProfileHeaderModal
                isOpen={visibleProfileHeaderModal}
                avatar={avatar}
                wallpaper={wallpaper}
                onChangeWallpaper={setWallpaper}
                onClose={handleCloseModal}
                onOpenProfileDescriptionModal={onOpenProfileDescriptionModal}
            />
            <ProfileDescriptionModal
                isOpen={visibleProfileDescriptionModal}
                onClose={handleCloseModal}
                text={bio}
                onChangeText={setBio}
                onOpenProfileUpdatedModal={onOpenProfileUpdatedModal}
            />
            <ProfileUpdatedModal
                isOpen={visibleProfileUpdatedModal}
                onClose={handleCloseModal}
                onSubmit={onSubmit}
                btnLabel={btnLabel}
            />
        </div>
    );
};

export default SetupProfileModal;
